/**
 * English (UK) messages used by the web portal (Phase 0 task T4).
 * Kept in its own file and spread into `enGB` so it merges cleanly with other tasks' additions.
 */
export const webEnGB = {
    // Errors
    'error.network': 'Cannot reach the server. Check your connection and try again.',
    // Common
    'common.all': 'All',
    'common.areYouSure': 'Are you sure?',
    'common.back': 'Back',
    'common.confirm': 'Confirm',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.pagination': 'Pages',
    'common.retry': 'Try again',
    'common.close': 'Close',
    'common.dismiss': 'Dismiss',
    'common.filters': 'Filters',
    'common.searchPlaceholder': 'Search',
    'common.notifications': 'Notifications',
    // Shell
    'shell.skipToContent': 'Skip to main content',
    'shell.openMenu': 'Open menu',
    'shell.closeMenu': 'Close menu',
    'shell.mainMenu': 'Main menu',
    'shell.switchOrganisation': 'Switch organisation',
    'shell.manage': 'Manage',
    'shell.profileMenu': 'Account menu',
    'shell.signedInAs': 'Signed in as',
    'shell.evacuate': 'Evacuate',
    'shell.settingsMenu': 'Manage settings',
    'shell.drawer': 'Site menu',
    // Menu descriptions (the nav label key plus '.desc'), also used as page descriptions
    'nav.home.desc': 'The latest at a glance.',
    'nav.users.desc': 'Invite people and manage who can sign in.',
    'nav.apps.desc': 'Install the CloudSign apps on your Android devices.',
    'nav.roles.desc': 'Decide what each role can see and do.',
    'nav.settings.desc': 'Branding, security and organisation settings.',
    'nav.emails.desc': 'Check the emails CloudSign has sent for you.',
    'nav.audit.desc': 'See who changed what, and when.',
    'nav.account.desc': 'Your details, password and two-factor sign-in.',
    'nav.platform.tenants.desc': 'Set up customers and choose which features they have.',
    'nav.platform.admins.desc': 'Choose who can run the whole platform.',
    'nav.platform.settings.desc': 'Defaults and security for every customer.',
    'nav.platform.jobs.desc': 'Watch background work and retry anything that failed.',
    'nav.platform.audit.desc': 'See every change made across the platform.',
    // Home
    'home.welcome': 'Welcome, {name}.',
    'home.moreComing': 'More features are coming soon.',
    'home.greeting': "Hi {firstName}, here's the latest at {tenant}",
    'home.stats': 'At a glance',
    'home.stat.users': 'Users',
    'home.stat.roles': 'Roles',
    'home.stat.invites': 'Pending invitations',
    'home.gettingStarted': 'Getting started',
    'home.gettingStartedHelp': 'A few things to do first. Live activity will appear here as features arrive.',
    'home.start.branding.title': 'Add your logo and colours',
    'home.start.branding.text': 'Make sign-in pages and emails look like your organisation.',
    'home.start.settings.title': 'Check your settings',
    'home.start.settings.text': 'Review security and organisation defaults.',
    'home.start.users.title': 'Invite your team',
    'home.start.users.text': 'Add the people who will help you run CloudSign.',
    'home.start.roles.title': 'Review roles',
    'home.start.roles.text': 'See what each role can do and create your own.',
    // Auth
    'auth.promoTitle': 'Know who is on site, always.',
    'auth.promoText': 'Sign visitors and staff in and out simply and safely, from one calm place.',
    'auth.verify': 'Verify',
    'auth.useAuthenticatorCode': 'Use my authenticator app instead',
    'auth.backToSignIn': 'Back to sign in',
    'auth.forgotTitle': 'Reset your password',
    'auth.sendResetLink': 'Send reset link',
    'auth.resetDone': 'Your password has been changed. You can now sign in.',
    'auth.qrAlt': 'QR code for your authenticator app',
    'auth.recoveryCodesSaved': 'I have saved these codes',
    'auth.regenerateCodes': 'Get new recovery codes',
    // Account
    'account.profile': 'Your details',
    'account.passwordChanged': 'Your password has been changed.',
    // Users
    'users.invite': 'Invite a user',
    'users.sendInvite': 'Send invitation',
    'users.role': 'Role',
    'users.twoFactor': 'Two-factor',
    'users.lastLogin': 'Last signed in',
    'users.never': 'Never',
    'users.details': 'Details',
    'users.actions': 'Actions',
    'users.actionDone': 'Done.',
    'users.resendInvite': 'Resend invitation',
    'users.sendPasswordReset': 'Send password reset email',
    'users.remove2fa': 'Remove two-factor',
    'users.delete': 'Delete user',
    'users.sitesLater': 'Choosing sites for this role arrives with the Sites feature.',
    // Roles
    'roles.create': 'Create a role',
    'roles.details': 'Role details',
    'roles.description': 'Description',
    'roles.siteScope': 'Sites',
    'roles.users': 'Users',
    'roles.builtin': 'Built in',
    'roles.builtinLocked': 'This is a built-in role and cannot be changed.',
    'roles.permissions': 'Permissions',
    'roles.permissionsHelp': 'You can only give permissions that you have yourself.',
    'roles.sensitive': 'Sensitive data',
    'roles.moduleOff': 'Feature off',
    'roles.cannotGrant': 'You do not have this permission, so you cannot give it.',
    'roles.selectGroup': 'Select all',
    'roles.clearGroup': 'Clear all',
    'roles.delete': 'Delete role',
    // Settings page
    'settings.discard': 'Discard changes',
    'settings.logoHelp': 'PNG, JPEG or SVG, up to 1 MB.',
    'settings.chooseLogo': 'Choose a logo file',
    'settings.uploadLogo': 'Upload logo',
    'settings.replaceLogo': 'Replace logo',
    'settings.removeLogo': 'Remove logo',
    'settings.preview': 'Preview',
    'settings.previewAccent': 'Accent colour',
    // Email log
    'emails.when': 'When',
    'emails.to': 'To',
    'emails.subject': 'Subject',
    'emails.attempts': 'Attempts',
    'emails.error': 'Last error',
    // Audit log
    'audit.when': 'When',
    'audit.who': 'Who',
    'audit.action': 'Action',
    'audit.entity': 'Item',
    'audit.tenantId': 'Organisation ID',
    'audit.from': 'From date',
    'audit.to': 'To date',
    'audit.ip': 'From address',
    'audit.showDetails': 'Show changes',
    'audit.hideDetails': 'Hide changes',
    'audit.changes': 'Changes',
    'audit.field': 'Field',
    'audit.before': 'Before',
    'audit.after': 'After',
    'audit.noDetails': 'No details were recorded for this entry.',
    // Platform: customers
    'tenants.create': 'Create a customer',
    'tenants.slug': 'Address name',
    'tenants.slugHelp': 'Lower-case letters, numbers and dashes. Used in web addresses.',
    'tenants.adminEmail': 'First administrator email',
    'tenants.adminName': 'First administrator name',
    'tenants.modules': 'Features',
    'tenants.noModules': 'There are no optional features yet.',
    'tenants.users': 'Users',
    'tenants.created': 'Created',
    'tenants.details': 'Details',
    'tenants.rename': 'Rename',
    'tenants.changeStatus': 'Change status',
    'tenants.suspendWarning': 'Suspending signs out everyone in this organisation straight away.',
    'tenants.usage': 'Usage',
    'tenants.month': 'Month',
    // Platform: admins and jobs
    'admins.add': 'Add a platform admin',
    'admins.remove': 'Remove',
    'admins.you': 'You',
    'jobs.name': 'Job',
    'jobs.attempts': 'Attempts',
    'jobs.runAt': 'Run at',
    'jobs.retry': 'Retry',
    // Usage metrics
    'usage.visits': 'Visits',
    'usage.sms_sent': 'Text messages sent',
    'usage.emails_sent': 'Emails sent',
    'usage.storage_bytes': 'Storage used',
    'usage.active_users': 'Active users',
    'usage.kiosk_devices': 'Kiosk devices',
    // Feature (module) names
    'module.sites': 'Sites',
    'module.groups': 'Groups',
    'module.members': 'Members',
    'module.visits': 'Visits',
    'module.kiosk-devices': 'Kiosk devices',
    'module.badges': 'Badges',
    'module.custom-fields': 'Custom fields',
    'module.notices': 'Notices',
    'module.agreements': 'Signed agreements',
    'module.evacuation': 'Evacuation',
    'module.reports': 'Reports',
    'module.notifications': 'Notifications',
    'module.pre-registration': 'Pre-registration',
    'module.contactless': 'Contactless sign-in',
    'module.sign-in-points': 'Sign-in points',
    'module.events-mgmt': 'Events',
    'module.safety-check': 'Safety check',
    'module.deliveries': 'Deliveries',
    'module.remote-site': 'Remote sites',
    'module.api-clients': 'Client API',
    'module.webhooks': 'Webhooks',
    'module.integrations': 'Integrations',
    'module.time-attendance': 'Time and attendance',
    'module.scheduling': 'Scheduling',
    'module.spaces': 'Spaces',
    'module.migration': 'Migration',
    'module.data-protection': 'Data protection',
    'module.compliance': 'Compliance',
    'module.permits': 'Permits to work',
    'module.lone-working': 'Lone working',
    'module.id-checks': 'ID checks',
    'module.reception-display': 'Reception display',
    'module.feedback': 'Visitor feedback',
    'module.insights': 'Trends and insights',
    'module.ask-reports': 'Ask-a-question reports',
    'module.omp-link': 'Oakford Mobile Portal link',
};
//# sourceMappingURL=web-en-GB.js.map